/**
 * I18n Loading Wrapper Component
 *
 * Prevents Flash of Untranslated Content (FOUC) by waiting for
 * translation resources to load before rendering children.
 *
 * Displays a minimal loading screen while i18n initializes.
 *
 * @module i18n/I18nLoadingWrapper
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isI18nReady } from './index';

interface I18nLoadingWrapperProps {
  children: React.ReactNode;
}

/**
 * Loading Screen Component
 *
 * Displays a centered loading spinner with minimal styling
 * Uses only CSS for maximum compatibility (no dependencies on translations)
 */
function LoadingScreen() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        flexDirection: 'column',
        gap: '1rem'
      }}
    >
      {/* Loading Spinner */}
      <div
        style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}
        role="status"
        aria-label="Loading translations"
      />

      {/* Loading Text - No translations needed */}
      <div
        style={{
          fontSize: '14px',
          color: '#6b7280',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
      >
        Loading...
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * I18n Loading Wrapper
 *
 * Waits for i18n to fully initialize before rendering children.
 * Prevents showing translation keys or untranslated content.
 *
 * Performance:
 * - Initial load: ~100-200ms (loading single language)
 * - Language switch: ~50-100ms (if resources not cached)
 * - Subsequent loads: Instant (resources already loaded)
 *
 * @param props - Component props
 * @returns Loading screen or children based on i18n state
 */
export function I18nLoadingWrapper({ children }: I18nLoadingWrapperProps) {
  const { ready: hookReady } = useTranslation();
  const [isReady, setIsReady] = useState(() => {
    // Check synchronously on mount - avoid flash if already ready
    return isI18nReady();
  });

  useEffect(() => {
    // Skip if already ready
    if (isReady) {
      return;
    }

    // Check if i18n is ready immediately (either our check or hook's ready)
    if (isI18nReady() || hookReady) {
      setIsReady(true);
      return;
    }

    let isMounted = true;
    const mountTimestamp = Date.now();

    // Fallback: Force ready after 2 seconds to prevent visible loading
    // Translation loading should complete in <500ms normally
    // This is a safety fallback for slow networks - app still works
    const timeoutId = setTimeout(() => {
      if (isMounted && !isReady) {
        const mountDuration = Date.now() - mountTimestamp;

        // Only warn if NOT a React StrictMode remount (which happens within ~10ms)
        // StrictMode remounts complete very quickly, so we ignore short durations
        if (mountDuration > 500 && import.meta.env.DEV) {
          console.warn('[i18n] Forcing ready state after timeout - i18n may not have initialized correctly');
        }
        // StrictMode cycles (<500ms) are expected - no need to log

        setIsReady(true);
      }
    }, 2000);

    // Poll every 50ms until ready (faster polling = less visible delay)
    const intervalId = setInterval(() => {
      if (!isMounted) {
        return;
      }

      // Accept either condition as "ready"
      if (isI18nReady() || hookReady) {
        setIsReady(true);
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      }
    }, 50);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [hookReady, isReady]);

  // Show loading screen while i18n initializes
  if (!isReady) {
    return <LoadingScreen />;
  }

  // Render children when ready
  return <>{children}</>;
}
